import {
  Button,
  Input,
  message,
  Modal,
  Table,
  TableColumnsType,
  TableProps,
  Tag,
  Tooltip,
} from 'antd';
import ReviewModal, { ReviewModalProps } from './ReviewModal';
import ScrollTable from 'components/ScrollTable';
import styled from '@emotion/styled';
import { FormOutlined } from '@ant-design/icons';
import useModal from 'hooks/useModal';
import StatusButton from 'components/StatusButton';
import { updateProblem } from './service';
import LogisticsDrawer from './LogisticsDrawer';
import RegionDrawer from './RegionDrawer';
import toLowerCase from 'utils/toLowerCase';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import useModeTranslate from './useModeTranslate';
import { useMemo } from 'react';
import { uniq, get } from 'lodash-es';
import { useQuery } from '@tanstack/react-query';

type RT = ReviewAnalysis.Problem;

function ProblemTable({
  isVoice,
  isItemDistribution,
  isAdvantage,
  item,
  isLogistics,
  sortOrder,
  onUpdate,
  dataSource,
  loading,
  ...otherProps
}: TableProps<RT> & {
  isVoice?: boolean;
  isItemDistribution?: boolean;
  isAdvantage?: boolean;
  item?: ReviewAnalysis.Item;
  isLogistics?: boolean;
  sortOrder?: 'DESC' | 'ASC';
  onUpdate: (problem: ReviewAnalysis.Problem[]) => void;
}) {
  const texts = useMemo(
    () => uniq(dataSource?.map((_) => _.content).filter((_) => _) || []),
    [dataSource],
  );

  const { t, trigger: translateTrigger, result: translateResult } = useModeTranslate(texts);
  const getRegionLabel = useGetRegionLabel();

  const {
    visible: remarkVisible,
    show: showRemark,
    hide: hideRemark,
    update: updateRemark,
    meta: remarkMeta,
  } = useModal<ReviewAnalysis.Problem>();

  const remarkModal = remarkMeta && (
    <Modal
      title={t('备注')}
      width={600}
      visible={remarkVisible}
      onCancel={() => hideRemark()}
      footer={
        <div>
          <Button onClick={() => hideRemark()}>{t('取消')}</Button>
          <StatusButton
            type="primary"
            onClick={() => {
              return updateProblem({
                id: remarkMeta.id,
                remark: remarkMeta.remark,
                tagStatus: remarkMeta.status,
                spuKey: item ? item.spuKey : undefined,
              })
                .then(() => {
                  dataSource &&
                    onUpdate(
                      dataSource?.map((cur) => {
                        return cur.id === remarkMeta.id ? remarkMeta : cur;
                      }),
                    );
                  hideRemark();
                  message.success(t('phrase.保存成功'));
                })
                .catch(() => {
                  message.error(t('phrase.保存失败，请稍后再试'));
                });
            }}
          >
            {t('保存')}
          </StatusButton>
        </div>
      }
    >
      <Input.TextArea
        showCount
        rows={8}
        placeholder={t('department.请输入x', { x: t('备注') })}
        maxLength={1000}
        value={remarkMeta?.remark}
        onChange={({ target: { value } }) =>
          remarkMeta &&
          updateRemark(true, {
            ...remarkMeta,
            remark: value,
          })
        }
      />
    </Modal>
  );

  const {
    visible: reviewVisible,
    show: showReview,
    hide: hideReview,
    meta: reviewMeta,
  } = useModal<ReviewModalProps>();

  const reviewModal = reviewMeta && (
    <ReviewModal
      key={reviewMeta.reviewIds.join('@')}
      isVoice={isVoice}
      {...reviewMeta}
      visible={reviewVisible}
      onCancel={() => hideReview()}
    />
  );

  const {
    visible: logisticsVisible,
    show: showLogistics,
    hide: hideLogistics,
    meta: logisticsMeta,
  } = useModal<RT>();

  const logisticsDrawer = logisticsMeta && (
    <LogisticsDrawer
      isVoice={isVoice}
      isAdvantage={isAdvantage}
      problem={logisticsMeta}
      visible={logisticsVisible}
      onClose={() => hideLogistics()}
    />
  );

  const {
    visible: regionVisible,
    show: showRegion,
    hide: hideRegion,
    meta: regionMeta,
  } = useModal<RT>();

  const regionDrawer = regionMeta && (
    <RegionDrawer
      key={regionMeta.id}
      isAdvantage={isAdvantage}
      problem={regionMeta}
      visible={regionVisible}
      onClose={() => hideRegion()}
      zIndex={1002}
    />
  );

  const { data: isoMap, isLoading: isoMapLoading } = useQuery({
    queryKey: ['isoMap'],
    queryFn: () => {
      return fetch('https://clients.zhisuitech.com/fett-public/geo/iso-map.json').then((res) =>
        res.json(),
      );
    },
  });

  const columns: TableColumnsType<RT> = [
    ...(isItemDistribution
      ? [
          {
            key: 'category',
            dataIndex: 'categoryId',
            title: t('feedback.分类'),
            width: 11,
            filters: [
              { value: 1, text: t('knowledge.商品相关') },
              { value: 2, text: t('物流配送') },
              { value: 3, text: t('服务体验') },
              { value: 4, text: t('活动营销') },
            ],
            onFilter: (value: any, record: RT) => record.categoryId === value,
            render: (categoryId: RT['categoryId']) => {
              return (
                {
                  1: t('knowledge.商品相关'),
                  2: t('物流配送'),
                  3: t('服务体验'),
                  4: t('活动营销'),
                }[categoryId] || '-'
              );
            },
          },
        ]
      : []),
    {
      key: 'content',
      title: (
        <div className="flex items-center justify-between flex-wrap">
          <div>{isAdvantage ? t('优点内容') : t('问题内容')}</div>
          {texts.length > 0 && <div>{translateTrigger}</div>}
        </div>
      ),
      width: 18,
      render: (_, { content, isNewProblem }) => (
        <WithTag>
          <div>
            {translateResult?.[content] ? (
              <Tooltip title={translateResult[content]}>
                <div>{translateResult[content]}</div>
              </Tooltip>
            ) : null}
            <Tooltip title={content}>
              <div>{content}</div>
            </Tooltip>
          </div>
          {isNewProblem && false && (
            <Tag color="red">{isAdvantage ? t('新优点') : t('新问题')}</Tag>
          )}
        </WithTag>
      ),
    },
    isItemDistribution
      ? {
          key: 'reviewCount',
          dataIndex: 'reviewCount',
          title: t('差评数'),
          width: 11,
          defaultSortOrder: 'descend',
          showSorterTooltip: false,
          sorter: (a, b) => a.reviewCount - b.reviewCount,
        }
      : {
          key: 'reviewCount',
          dataIndex: 'reviewCount',
          title: isVoice ? t('评价数') : t('差评数'),
          width: 11,
          sorter: sortOrder && true,
          showSorterTooltip: false,
          sortOrder:
            sortOrder &&
            (
              {
                DESC: 'descend',
                ASC: 'ascend',
              } as const
            )[sortOrder],
        },
    ...(item
      ? []
      : [
          {
            key: 'itemCount',
            dataIndex: 'itemCount',
            title: t('涉及商品数'),
            width: 11,
          },
        ]),
    ...(isLogistics
      ? [
          {
            key: 'logistics',
            title: isVoice
              ? isAdvantage
                ? t('优势物流TOP2')
                : t('问题物流TOP2')
              : t('差评物流TOP2'),
            width: 20,
            render: (_: any, record: RT) => {
              const {
                shipmentProvider: [
                  { name, percent } = {},
                  { name: name2, percent: percent2 } = {},
                ] = [],
              } = record;
              return (
                <div>
                  <div>
                    {name ? `${name}(${percent}%)` : undefined}
                    {name2 ? `  |  ${name2}(${percent2}%)` : undefined}
                  </div>
                  <Button
                    className="!pl-0"
                    type="link"
                    onClick={() =>
                      showLogistics({
                        ...record,
                        cnContent: translateResult?.[record.content],
                      })
                    }
                  >
                    {isVoice
                      ? isAdvantage
                        ? t('优势物流明细')
                        : t('问题物流明细')
                      : t('差评物流明细')}
                  </Button>
                </div>
              );
            },
          },
          {
            key: 'region',
            title: isVoice
              ? isAdvantage
                ? t('优势地区TOP2')
                : t('问题地区TOP2')
              : t('差评地区TOP2'),
            width: 28,
            render: (_: any, record: RT) => {
              if (!record.regionData.length) {
                return '-';
              }
              const {
                regionData: [
                  { receiveCountryCode, receiveStateCode, percent } = {},
                  {
                    receiveCountryCode: country2,
                    receiveStateCode: state2,
                    percent: percent2,
                  } = {},
                ] = [],
              } = record;
              if (isoMapLoading) return null;
              return (
                <div>
                  <div>
                    {receiveCountryCode
                      ? `${getRegionLabel(receiveCountryCode, receiveCountryCode)} - ${
                          get(
                            isoMap,
                            `[${toLowerCase(receiveCountryCode)}].[${receiveStateCode as any}]`,
                          ) ||
                          get(isoMap, `[${receiveCountryCode}].[${receiveStateCode as any}]`) ||
                          receiveStateCode
                        }(${percent}%)`
                      : undefined}
                    {country2
                      ? `  |  ${getRegionLabel(country2, country2)} - ${
                          get(isoMap, `[${toLowerCase(country2)}].[${state2 as any}]`) ||
                          get(isoMap, `[${country2}].[${state2 as any}]`) ||
                          state2
                        }(${percent2}%)`
                      : undefined}
                  </div>
                  <Button
                    className="!pl-0"
                    type="link"
                    onClick={() =>
                      showRegion({
                        ...record,
                        cnContent: translateResult?.[record.content],
                      })
                    }
                  >
                    {isVoice
                      ? isAdvantage
                        ? t('优势地区分布明细')
                        : t('问题地区分布明细')
                      : t('差评地区分布明细')}
                  </Button>
                </div>
              );
            },
          },
        ]
      : []),
    {
      key: 'remark',
      dataIndex: 'remark',
      title: t('备注'),
      width: 18,
      ellipsis: true,
      render: (remark: string, record) => (
        <WithButton>
          {remark ? (
            <Tooltip title={remark}>
              <div>{remark}</div>
            </Tooltip>
          ) : (
            <div>-</div>
          )}
          <Button type="text" icon={<FormOutlined />} onClick={() => showRemark(record)} />
        </WithButton>
      ),
    },
    {
      key: 'operation',
      title: t('操作'),
      width: 11,
      render: (_, record) => {
        const { reviewIds, reviewCount, content, regionData } = record;
        return (
          <Button
            className="pl-0"
            type="link"
            onClick={() =>
              showReview({
                reviewIds,
                count: reviewCount,
                item,
                problem: translateResult?.[content] || content,
                extra: isLogistics && regionData.length > 0 && (
                  <Button type="link" onClick={() => showRegion(record)}>
                    {isVoice
                      ? isAdvantage
                        ? t('优势地区分布明细')
                        : t('问题地区分布明细')
                      : t('差评地区分布明细')}
                  </Button>
                ),
              })
            }
          >
            {isVoice ? t('查看评价') : isItemDistribution ? t('查看差评') : t('差评明细')}
          </Button>
        );
      },
    },
  ];

  const Component = item ? ScrollTable : Table;

  const table = (
    <Component
      rowKey="id"
      columns={columns}
      dataSource={dataSource}
      loading={loading || isoMapLoading}
      {...otherProps}
    />
  );

  return (
    <>
      {table}
      {remarkModal}
      {reviewModal}
      {logisticsDrawer}
      {regionDrawer}
    </>
  );
}

const WithTag = styled.div`
  display: flex;
  align-items: flex-start;
  overflow: hidden;
  width: 100%;
  > div {
    overflow: hidden;
    margin-right: 4px;
    > div {
      white-space: pre-wrap;
    }
  }
`;

const WithButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  > div {
    flex: 1 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export default ProblemTable;
